import React from 'react';
import {
  IconChevronLeft,
  IconChevronRight,
  IconChevronUp,
  IconChevronDown,
} from '@tabler/icons-react';
import styles from './DataTable.module.css';

/**
 * Shared data-table kit — the app-standard list table (extracted from the
 * Search results table). Thin presentational wrappers over the native table
 * elements; pages keep their own per-cell rendering. Compose as:
 *
 *   <DataTableCard>
 *     <DataTable>
 *       <thead><tr><DataTableHeadCell .../>…</tr></thead>
 *       <tbody>{rows.map(r => <DataTableRow>…<DataTableCell/>…</DataTableRow>)}</tbody>
 *     </DataTable>
 *     <DataTablePagination … />
 *   </DataTableCard>
 */

type Align = 'left' | 'right' | 'center';

const cx = (...parts: Array<string | false | undefined>): string =>
  parts.filter(Boolean).join(' ');

/** White card wrapper for a table + its pagination footer (floats on the grey surface). */
export const DataTableCard: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={styles.card}>{children}</div>
);

/**
 * `fixed` opts into `table-layout: fixed` so column widths come from the
 * `width` props on the head cells instead of content — columns stay static
 * across pages. Body cells then truncate with an ellipsis. Default (auto)
 * layout is unchanged for every other table.
 */
export const DataTable: React.FC<{ children: React.ReactNode; fixed?: boolean }> = ({
  children,
  fixed = false,
}) => (
  <table className={cx(styles.table, fixed && styles.tableFixed)}>{children}</table>
);

interface HeadCellProps {
  children?: React.ReactNode;
  align?: Align;
  sortable?: boolean;
  active?: boolean;
  direction?: 'asc' | 'desc';
  onSort?: () => void;
  width?: number | string;
}

export const DataTableHeadCell: React.FC<HeadCellProps> = ({
  children,
  align = 'left',
  sortable = false,
  active = false,
  direction = 'asc',
  onSort,
  width,
}) => (
  <th
    className={cx(
      styles.hdr,
      align === 'right' && styles.hdrRight,
      align === 'center' && styles.hdrCenter,
      sortable && styles.hdrSortable,
    )}
    style={width !== undefined ? { width } : undefined}
    onClick={sortable ? onSort : undefined}
  >
    <span className={styles.hdrInner}>
      {children}
      {sortable && active && (
        <span className={styles.sortIcon}>
          {direction === 'asc'
            ? <IconChevronUp size={13} stroke={2} />
            : <IconChevronDown size={13} stroke={2} />}
        </span>
      )}
    </span>
  </th>
);

interface RowProps {
  children: React.ReactNode;
  onClick?: () => void;
}

export const DataTableRow: React.FC<RowProps> = ({ children, onClick }) => (
  <tr className={cx(styles.row, onClick && styles.rowClickable)} onClick={onClick}>
    {children}
  </tr>
);

interface CellProps {
  children?: React.ReactNode;
  align?: Align;
  muted?: boolean;
  paid?: boolean;
  bold?: boolean;
  colSpan?: number;
  /** Escape hatch for one-off cell styling (e.g. a conditional red amount). */
  style?: React.CSSProperties;
}

export const DataTableCell: React.FC<CellProps> = ({
  children,
  align = 'left',
  muted = false,
  paid = false,
  bold = false,
  colSpan,
  style,
}) => (
  <td
    className={cx(
      styles.cell,
      align === 'right' && styles.cellRight,
      align === 'center' && styles.cellCenter,
      muted && styles.cellMuted,
      paid && styles.cellPaid,
      bold && styles.cellBold,
    )}
    colSpan={colSpan}
    style={style}
  >
    {children}
  </td>
);

/** Full-width centred message row (no results / hint). */
export const DataTableEmpty: React.FC<{ colSpan: number; children: React.ReactNode }> = ({
  colSpan,
  children,
}) => (
  <tr>
    <td className={styles.empty} colSpan={colSpan}>{children}</td>
  </tr>
);

interface PaginationProps {
  /** 0-based page index (matches MUI's convention). */
  page: number;
  rowsPerPage: number;
  count: number;
  rowsPerPageOptions?: number[];
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

export const DataTablePagination: React.FC<PaginationProps> = ({
  page,
  rowsPerPage,
  count,
  rowsPerPageOptions = [25, 50, 100],
  onPageChange,
  onRowsPerPageChange,
}) => {
  const start = count === 0 ? 0 : page * rowsPerPage + 1;
  const end = Math.min((page + 1) * rowsPerPage, count);
  const hasNext = (page + 1) * rowsPerPage < count;
  return (
    <div className={styles.footer}>
      <span>Rows per page:</span>
      <select
        className={styles.rpp}
        value={rowsPerPage}
        onChange={(e) => onRowsPerPageChange(Number(e.target.value))}
      >
        {rowsPerPageOptions.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
      <span>{start}–{end} of {count}</span>
      <button
        type="button"
        className={styles.pageBtn}
        onClick={() => onPageChange(page - 1)}
        disabled={page === 0}
        aria-label="Previous page"
      >
        <IconChevronLeft size={16} stroke={1.5} />
      </button>
      <button
        type="button"
        className={styles.pageBtn}
        onClick={() => onPageChange(page + 1)}
        disabled={!hasNext}
        aria-label="Next page"
      >
        <IconChevronRight size={16} stroke={1.5} />
      </button>
    </div>
  );
};
